import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, poundsFromPence, formatErr } from "../lib/api";
import { useEvent } from "../lib/event";
import { useAttraction } from "../lib/attraction";
import { formatSlotTime } from "../lib/timeFormat";
import DashboardLayout from "../components/DashboardLayout";
import WizardBanner from "../components/WizardBanner";
import CompactCalendar, { pickSmartDefault } from "../components/CompactCalendar";
import { CardListSkeleton } from "../components/Skeleton";
import { CapacityNote } from "../components/CapacityNote";
import { Settings as SettingsIcon, X, RotateCcw, AlertTriangle, Ticket as TicketIcon, Plus, Pause, Play, Trash2, Coins, Users, Award, Moon, TrendingUp, Info, ArrowRight } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer } from "recharts";
import { confirmDialog } from "../components/ConfirmDialog";

function formatLongDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function availColor(available, capacity) {
  if (capacity <= 0) return "text-[#7a7a7a]";
  const pct = (available / capacity) * 100;
  if (available === 0) return "text-[#fca5a5]";
  if (pct <= 25) return "text-[#dc2626]";
  if (pct <= 50) return "text-[#fbbf24]";
  return "text-[#dadada]";
}

function Toast({ text, kind, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className={`fixed top-5 right-5 z-[80] px-4 py-3 rounded-lg border st-fade-up ${
      kind === "err" ? "bg-[#2a1212] border-[#5b1919] text-[#fca5a5]" : "bg-[#0c1d12] border-[#1b3a25] text-[#86efac]"
    }`} data-testid={`toast-${kind}`}>
      {text}
    </div>
  );
}

export default function TicketsPage() {
  const { currentEvent, currentEventId } = useEvent();
  const { attraction } = useAttraction() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const [eventData, setEventData] = useState(null);  // dates list for calendar
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [perf, setPerf] = useState(null);            // perf data for selected date
  const [selectedDateId, setSelectedDateId] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState(null);  // null = All types
  const [bulkOpen, setBulkOpen] = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);  // {kind:'slot'|'date', obj, basePrice, defaultCap}
  const [addSlotOpen, setAddSlotOpen] = useState(false);
  const [msg, setMsg] = useState(""); const [err, setErr] = useState("");
  const [series, setSeries] = useState(null);      // {day:[], week:[]}
  const [chartMode, setChartMode] = useState("day");

  // Load event (for date list)
  useEffect(() => {
    // Clear stale date/type/perf immediately so the perf loader doesn't fire a 404
    // against the previous event's date while the new event loads.
    setSelectedDateId(null);
    setSelectedTypeId(null);
    setPerf(null);
    (async () => {
      if (!currentEventId) { setEventData(null); setLoadingEvent(false); return; }
      setLoadingEvent(true);
      try {
        const r = await api.get(`/events/${currentEventId}`);
        setEventData(r.data);
        setSelectedTypeId(null);  // default to All types so figures match Open Days
        // Smart default date selection (honour ?date= if it points to a real date)
        const dates = (r.data.dates || []).filter(d => d.status !== "cancelled" && !d.is_blocked);
        const requested = searchParams.get("date");
        const match = requested && (r.data.dates || []).find(d => d.id === requested);
        if (match) {
          setSelectedDateId(match.id);
          const sp = new URLSearchParams(searchParams); sp.delete("date"); setSearchParams(sp, { replace: true });
        } else {
          const pick = pickSmartDefault(dates);
          setSelectedDateId(pick?.id || null);
        }
      } catch (e) { setErr("Failed to load event"); }
      finally { setLoadingEvent(false); }
    })();
    setSeries(null);
    if (currentEventId) {
      api.get(`/events/${currentEventId}/sales-timeseries`).then((r) => setSeries(r.data)).catch(() => setSeries(null));
    }
  }, [currentEventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load performance for selected date
  const loadPerf = async () => {
    if (!currentEventId || !selectedDateId) { setPerf(null); return; }
    // Guard against a stale date from a previous event (fires during event switch).
    if (eventData && !(eventData.dates || []).some(d => d.id === selectedDateId)) { setPerf(null); return; }
    try {
      const q = selectedTypeId ? `?ticket_type_id=${selectedTypeId}` : "";
      const r = await api.get(`/events/${currentEventId}/dates/${selectedDateId}/performance${q}`);
      setPerf(r.data);
    } catch (e) { if (e?.response?.status !== 404) setErr("Failed to load performance"); setPerf(null); }
  };
  useEffect(() => { loadPerf(); /* eslint-disable-line */ }, [selectedDateId, currentEventId, selectedTypeId]);
  // Close any open override panel when switching ticket-type tabs (labels are type-specific).
  useEffect(() => { setOverrideTarget(null); }, [selectedTypeId]);

  if (!currentEventId) {
    return <DashboardLayout title="Tickets">
      <div className="st-card p-12 text-center" data-testid="no-event-prompt">
        <TicketIcon className="mx-auto text-[#dc2626]"/>
        <h3 className="text-xl font-bold mt-4">No event selected yet</h3>
        <p className="text-[#8a8a8a] mt-1">Use the event switcher above to create one.</p>
      </div>
    </DashboardLayout>;
  }

  const dates = eventData?.dates || [];
  const hasNothing = eventData && dates.length === 0 && (eventData.ticket_types || []).length === 0;

  return (
    <DashboardLayout title="Tickets" subtitle={currentEvent ? `${currentEvent.name} · live performance` : ""}
      actions={
        <div className="flex gap-2">
          {perf?.is_time_slotted && (
            <button className="st-btn st-btn-ghost" onClick={() => setAddSlotOpen(true)} data-testid="add-time-slot-btn">
              <Plus size={14}/> Add Time Slot
            </button>
          )}
          <Link to="/dashboard/tickets/types" className="st-btn st-btn-ghost" data-testid="manage-types-btn">
            <SettingsIcon size={14}/> Manage Ticket Types
          </Link>
        </div>
      }>
      <WizardBanner eventId={currentEventId} show={hasNothing} />
      {msg && <Toast text={msg} kind="ok" onDone={() => setMsg("")} />}
      {err && <Toast text={err} kind="err" onDone={() => setErr("")} />}

      {(perf?.ticket_types || []).length > 0 && (
        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1" data-testid="ticket-type-tabs">
          <button onClick={() => setSelectedTypeId(null)} data-testid="type-tab-all"
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${selectedTypeId === null ? "bg-[#dc2626] border-[#dc2626] text-white" : "bg-[#0e0e0e] border-[#222] text-[#bbb] hover:border-[#dc2626]"}`}>
            All types
          </button>
          {perf.ticket_types.map((t) => (
            <button key={t.id} onClick={() => setSelectedTypeId(t.id)} data-testid={`type-tab-${t.id}`}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border transition-colors ${selectedTypeId === t.id ? "bg-[#dc2626] border-[#dc2626] text-white" : "bg-[#0e0e0e] border-[#222] text-[#bbb] hover:border-[#dc2626]"}`}>
              {t.name}
            </button>
          ))}
          {perf?.is_time_slotted && selectedTypeId && (
            <button onClick={() => setBulkOpen(true)} data-testid="bulk-edit-btn"
                    className="ml-auto px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap border border-[#333] text-[#dadada] hover:border-[#dc2626] hover:text-white inline-flex items-center gap-1.5 flex-shrink-0">
              <SettingsIcon size={12} /> Bulk edit
            </button>
          )}
        </div>
      )}

      {dates.length === 0 ? (
        loadingEvent ? (
          <CardListSkeleton rows={4} testid="tickets-skeleton" />
        ) : (
        <div className="st-card p-12 text-center" data-testid="tickets-no-dates">
          <TicketIcon className="mx-auto text-[#dc2626]"/>
          <h3 className="text-xl font-bold mt-4">No open days yet</h3>
          <p className="text-[#8a8a8a] mt-1 mb-6">Add dates first to see live performance here.</p>
          <Link to="/dashboard/open-days" className="st-btn inline-flex">Go to Open Days</Link>
        </div>
        )
      ) : (
        <>
          {perf && <TicketsSummary perf={perf} attraction={attraction} selectedTypeId={selectedTypeId} />}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            {/* Left — Performance */}
            <div data-testid="tickets-perf" className="lg:col-span-3">
              {perf ? (
                <PerformanceView perf={perf} basePrice={perf.base_price_pence} attraction={attraction} eventId={currentEventId}
                  selectedTypeId={selectedTypeId}
                  onOpenOverride={(kind, obj) => setOverrideTarget({ kind, obj, basePrice: perf.base_price_pence, ticketTypeId: selectedTypeId })}
                  onReload={loadPerf}
                  onMsg={setMsg} onErr={setErr} />
              ) : (
                <div className="st-card p-10 text-center text-[#8a8a8a]">Loading…</div>
              )}
            </div>

            {/* Right — Compact calendar + insights */}
            <div className="lg:sticky lg:top-6 lg:self-start lg:col-span-1 space-y-3">
              <CompactCalendar
                dates={dates}
                mode="selection"
                selectedDate={perf?.date?.date}
                onSelect={(d) => setSelectedDateId(d.id)}
                testid="tickets-calendar"
              />
              <SalesChart series={series} mode={chartMode} onMode={setChartMode} />
              <QuickInsights perf={perf} basePrice={perf?.base_price_pence} />
            </div>
          </div>
        </>
      )}

      {overrideTarget && (
        <OverridePanel
          target={overrideTarget}
          attraction={attraction}
          eventId={currentEventId}
          dateHardCap={perf?.date?.hard_cap}
          siblings={(perf?.time_slots || []).filter(s => s.id !== overrideTarget.obj?.id)}
          onClose={() => setOverrideTarget(null)}
          onSaved={() => { setOverrideTarget(null); setMsg("Override saved"); loadPerf(); }}
          onMsg={setMsg}
          onErr={setErr}
        />
      )}

      {bulkOpen && selectedTypeId && perf && (
        <BulkEditModal
          eventId={currentEventId}
          ticketType={(perf.ticket_types || []).find(t => t.id === selectedTypeId)}
          dateId={perf.date.id}
          dateLabel={formatLongDate(perf.date.date)}
          slots={perf.time_slots || []}
          attraction={attraction}
          onClose={() => setBulkOpen(false)}
          onSaved={(n) => { setBulkOpen(false); setMsg(`Bulk update applied to ${n} slot${n === 1 ? "" : "s"}`); loadPerf(); }}
          onErr={setErr}
        />
      )}

      {addSlotOpen && perf?.is_time_slotted && perf?.date && (
        <AddSlotModal eventId={currentEventId} dateId={perf.date.id} attraction={attraction}
                      onClose={() => setAddSlotOpen(false)}
                      onSaved={() => { setMsg("Slot added"); loadPerf(); }}
                      onErr={(e) => setErr(e)} />
      )}
    </DashboardLayout>
  );
}

function TStat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="st-card p-4 flex items-start justify-between gap-2" data-testid={testid}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">{label}</div>
        <div className="text-2xl font-bold leading-tight mt-1 truncate">{value}</div>
        {sub && <div className="text-xs text-[#7a7a7a] mt-0.5">{sub}</div>}
      </div>
      <div className="w-10 h-10 rounded-xl bg-[#141414] border border-[#1e1e1e] flex items-center justify-center shrink-0">
        <Icon size={17} className="text-[#dc2626]" />
      </div>
    </div>
  );
}

function SalesChart({ series, mode, onMode }) {
  const data = (series && series[mode]) || [];
  const hasData = data.some((d) => d.value > 0);
  return (
    <div className="st-card p-4" data-testid="ticket-sales-chart">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">Ticket sales (all time)</div>
        <div className="flex items-center gap-1 bg-[#0f0f0f] border border-[#1e1e1e] rounded-full p-0.5">
          {["day", "week"].map((m) => (
            <button key={m} onClick={() => onMode(m)} data-testid={`chart-mode-${m}`}
              className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize transition-colors ${mode === m ? "bg-[#dc2626] text-white" : "text-[#8a8a8a] hover:text-white"}`}>
              {m}
            </button>
          ))}
        </div>
      </div>
      {!series ? (
        <div className="h-[150px] flex items-center justify-center text-xs text-[#7a7a7a]">Loading…</div>
      ) : !hasData ? (
        <div className="h-[150px] flex items-center justify-center text-xs text-[#7a7a7a]">No sales in this period yet.</div>
      ) : (
        <div className="h-[150px]" data-testid="sales-chart-canvas">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 6, right: 6, left: -22, bottom: 0 }}>
              <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#6a6a6a" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9, fill: "#6a6a6a" }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
              <RTooltip contentStyle={{ background: "#0e0e0e", border: "1px solid #1e1e1e", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#dadada" }} itemStyle={{ color: "#dc2626" }} />
              <Line type="monotone" dataKey="value" name="Tickets" stroke="#dc2626" strokeWidth={2} dot={{ r: 2.5, fill: "#dc2626" }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function InsightRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#161616] last:border-0">
      <div className="flex items-center gap-2 text-sm text-[#bbb]">
        <Icon size={15} className="text-[#7a7a7a]" /> {label}
      </div>
      <div className="text-sm font-semibold text-right">{value}</div>
    </div>
  );
}

function QuickInsights({ perf, basePrice }) {
  if (!perf) return null;
  const slots = perf.time_slots || [];
  const cap = perf.date.effective_capacity;
  const sold = perf.date.tickets_sold;
  const pct = cap && cap > 0 ? Math.round((sold / cap) * 100) : 0;
  const sortable = slots.filter((s) => !s.is_paused);
  const best = sortable.length ? sortable.reduce((a, b) => (b.tickets_sold > a.tickets_sold ? b : a)) : null;
  const quiet = sortable.length ? sortable.reduce((a, b) => (b.tickets_sold < a.tickets_sold ? b : a)) : null;
  const potential = cap != null ? cap * (basePrice || 0) : null;
  const fmtSlot = (s) => (s ? s.start_time : "—");
  return (
    <div className="st-card p-4" data-testid="quick-insights">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a] mb-1">Quick insights</div>
      <InsightRow icon={Users} label={perf.date.capacity_detail?.per_ticket_type ? `${perf.date.capacity_detail.per_ticket_type} capacity` : "Total capacity"}
        value={<span className="inline-block text-right">
          {cap == null ? "∞" : cap.toLocaleString("en-GB")}
          <CapacityNote detail={perf.date.capacity_detail} testid="insights-capacity-note" />
        </span>} />
      <InsightRow icon={TicketIcon} label="Total sold" value={<span>{sold} <span className="text-[#7a7a7a] font-normal">({pct}%)</span></span>} />
      <InsightRow icon={Award} label="Best selling slot" value={best && best.tickets_sold > 0 ? `${fmtSlot(best)} · ${best.tickets_sold}` : "—"} />
      <InsightRow icon={Moon} label="Quietest slot" value={quiet && sold > 0 ? `${fmtSlot(quiet)} · ${quiet.tickets_sold}` : "—"} />
      <InsightRow icon={Coins} label="Revenue potential" value={potential == null ? "—" : poundsFromPence(potential)} />
      <Link to="/dashboard/reports" className="inline-flex items-center gap-1 text-xs font-semibold text-[#dc2626] hover:text-[#ef4444] mt-3" data-testid="view-reports-link">
        View detailed reports <ArrowRight size={13} />
      </Link>
    </div>
  );
}


function TicketsSummary({ perf, attraction, selectedTypeId }) {
  const isSlotted = perf.is_time_slotted;
  const perType = !!selectedTypeId;
  const typeName = (perf.ticket_types || []).find(t => t.id === selectedTypeId)?.name;
  const totalSold = perf.date.tickets_sold;
  const totalRev = perf.date.revenue_pence;
  const dateCap = perf.date.effective_capacity;       // may be null = unlimited (per-type)
  const unlimited = dateCap == null;
  const fill = !unlimited && dateCap > 0 ? Math.min(100, Math.round((totalSold / dateCap) * 100)) : 0;
  const available = perf.date.available;               // null = unlimited
  const avgPerTicket = totalSold > 0 ? Math.round(totalRev / totalSold) : 0;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3" data-testid="tickets-summary">
      <div className="st-card p-4" data-testid="date-summary-card">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#dc2626]">{isSlotted ? "Time-slotted" : "Single session"}{perType ? ` · ${typeName}` : ""}</div>
        <div className="text-lg font-bold leading-tight mt-1">{formatLongDate(perf.date.date)}</div>
        <div className="text-xs text-[#8a8a8a] mt-1">{formatSlotTime(perf.date.open_time, perf.date.close_time, attraction, "openHours")}</div>
        {perf.date.hard_cap != null && <div className="text-[10px] text-[#facc15] mt-1">Hard cap {perf.date.hard_cap}</div>}
      </div>
      <TStat icon={TicketIcon} label="Total available" testid="stat-available"
        value={available == null ? "∞" : available.toLocaleString("en-GB")}
        sub={<>
          {unlimited ? "No cap set" : `of ${dateCap.toLocaleString("en-GB")} capacity`}
          <CapacityNote detail={perf.date.capacity_detail} testid="summary-capacity-note" />
        </>} />
      <TStat icon={TrendingUp} label="Sold" testid="stat-sold"
        value={totalSold.toLocaleString("en-GB")}
        sub={<span><span className="text-[#dc2626] font-semibold">{fill}%</span>{unlimited ? "" : ` of ${dateCap}`}</span>} />
      <TStat icon={Coins} label="Revenue" testid="stat-revenue"
        value={poundsFromPence(totalRev)}
        sub={`Avg ${poundsFromPence(avgPerTicket)} per ticket`} />
    </div>
  );
}

function PerformanceView({ perf, basePrice, attraction, eventId, selectedTypeId, onOpenOverride, onReload, onMsg, onErr }) {
  const isSlotted = perf.is_time_slotted;

  return (
    <div>
      {isSlotted ? (
        <>
        <div className="st-card overflow-hidden hidden lg:block" data-testid="slot-list">
          <div className="grid grid-cols-[0.9fr_0.8fr_0.5fr_0.7fr_0.7fr_0.8fr_1.1fr_0.4fr] gap-3 px-5 py-3 text-[10px] uppercase tracking-wider text-[#7a7a7a] bg-[#0c0c0c] border-b border-[#1a1a1a]">
            <div>Time slot</div><div>Status</div><div>Sold</div><div>Available</div><div>Price</div><div>Revenue</div><div>Occupancy</div><div className="text-right">Actions</div>
          </div>
          {perf.time_slots.length === 0 && <div className="p-6 text-center text-[#8a8a8a]">No time slots for this date.</div>}
          {perf.time_slots.map(s => {
            const avail = s.available;
            const eff = s.effective_capacity;
            const overridden = s.has_override;
            const paused = s.is_paused;
            const priceOverridden = s.price_override_pence != null;
            const occ = eff && eff > 0 ? Math.min(100, Math.round((s.tickets_sold / eff) * 100)) : 0;
            return (
              <div key={s.id} className={`grid grid-cols-[0.9fr_0.8fr_0.5fr_0.7fr_0.7fr_0.8fr_1.1fr_0.4fr] gap-3 px-5 py-3 items-center border-b border-[#161616] hover:bg-[#0e0e0e] ${paused ? "opacity-60" : ""}`} data-testid={`slot-row-${s.id}`}>
                <div className="font-semibold st-mono text-sm">{formatSlotTime(s.start_time, s.end_time, attraction)}</div>
                <div>
                  {s.disabled ? <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#1a0d0d] text-[#fca5a5] border border-[#5b1f1f]" data-testid={`slot-unavailable-${s.id}`}>Not available</span>
                    : paused ? <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#1f1f1f] text-[#bbb] border border-[#333]">Paused</span>
                    : overridden ? <span className="text-[9px] px-1.5 py-0.5 rounded-md" style={{ background: "#1a1409", color: "#facc15", border: "1px solid #facc1544" }}>Overridden</span>
                    : <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#0f1a0f] text-[#86efac] border border-[#1f3d1f]">Standard</span>}
                </div>
                <div className="font-bold">{s.tickets_sold}</div>
                <div className={`font-semibold ${s.disabled ? "" : availColor(avail, eff)}`}>
                  {s.disabled ? <span className="text-[#8a6a6a]">Not available</span>
                    : eff == null ? <span className="text-[#7a7a7a]">No limit set</span>
                    : avail === 0 ? <span className="st-chip st-chip-red">Sold out</span> : avail}
                </div>
                <div className={priceOverridden ? "text-[#dc2626] font-semibold" : "text-[#dadada]"}>{poundsFromPence(s.effective_price_pence)}</div>
                <div className="text-[#dadada] font-semibold">{poundsFromPence(s.revenue_pence)}</div>
                <div className="flex items-center gap-2">
                  {eff == null ? <span className="text-[10px] text-[#7a7a7a]">—</span> : (
                    <>
                      <div className="flex-1 h-1.5 bg-[#161616] rounded-full overflow-hidden min-w-[40px]">
                        <div className="h-full bg-gradient-to-r from-[#dc2626] to-[#7f1d1d]" style={{ width: `${occ}%` }} />
                      </div>
                      <span className="text-[10px] text-[#8a8a8a] w-8 text-right">{occ}%</span>
                    </>
                  )}
                </div>
                <div className="text-right">
                  <button className="text-[#7a7a7a] hover:text-[#dc2626] p-1.5 rounded-md hover:bg-[#161616]"
                          onClick={() => onOpenOverride("slot", s)} data-testid={`override-slot-${s.id}`} aria-label="Override slot">
                    <SettingsIcon size={14}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile — slot cards */}
        <div className="lg:hidden space-y-3" data-testid="slot-list-mobile">
          {perf.time_slots.length === 0 && <div className="st-card p-6 text-center text-[#8a8a8a]">No time slots for this date.</div>}
          {perf.time_slots.map(s => {
            const avail = s.available;
            const eff = s.effective_capacity;
            const overridden = s.has_override;
            const paused = s.is_paused;
            const priceOverridden = s.price_override_pence != null;
            const occ = eff && eff > 0 ? Math.min(100, Math.round((s.tickets_sold / eff) * 100)) : 0;
            return (
              <div key={s.id} className={`st-card p-4 ${paused ? "opacity-60" : ""}`} data-testid={`slot-card-${s.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold st-mono text-sm">{formatSlotTime(s.start_time, s.end_time, attraction)}</div>
                  <div className="flex items-center gap-2">
                    {s.disabled ? <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#1a0d0d] text-[#fca5a5] border border-[#5b1f1f]" data-testid={`slot-unavailable-m-${s.id}`}>Not available</span>
                      : paused ? <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#1f1f1f] text-[#bbb] border border-[#333]">Paused</span>
                      : overridden ? <span className="text-[9px] px-1.5 py-0.5 rounded-md" style={{ background: "#1a1409", color: "#facc15", border: "1px solid #facc1544" }}>Overridden</span>
                      : <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-[#0f1a0f] text-[#86efac] border border-[#1f3d1f]">Standard</span>}
                    <button className="text-[#7a7a7a] hover:text-[#dc2626] p-1.5 rounded-md hover:bg-[#161616]"
                            onClick={() => onOpenOverride("slot", s)} data-testid={`override-slot-m-${s.id}`} aria-label="Override slot">
                      <SettingsIcon size={16}/>
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">Sold</div><div className="font-bold">{s.tickets_sold}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">Available</div>
                    <div className={`font-semibold ${s.disabled ? "" : availColor(avail, eff)}`}>
                      {s.disabled ? <span className="text-[#8a6a6a]">Not available</span>
                        : eff == null ? <span className="text-[#7a7a7a]">No limit set</span>
                        : avail === 0 ? <span className="st-chip st-chip-red">Sold out</span> : avail}
                    </div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">Price</div><div className={priceOverridden ? "text-[#dc2626] font-semibold" : "text-[#dadada]"}>{poundsFromPence(s.effective_price_pence)}</div></div>
                  <div><div className="text-[10px] uppercase tracking-wider text-[#7a7a7a]">Revenue</div><div className="text-[#dadada] font-semibold">{poundsFromPence(s.revenue_pence)}</div></div>
                </div>
                {eff != null && (
                  <div className="mt-3 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-[#161616] rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#dc2626] to-[#7f1d1d]" style={{ width: `${occ}%` }} />
                    </div>
                    <span className="text-[10px] text-[#8a8a8a] w-10 text-right">{occ}%</span>
                  </div>
                )}
                {s.type_caps?.length > 0 && (
                  <div className="mt-1.5 text-[10px] text-[#7a7a7a]" data-testid={`slot-type-caps-${s.id}`}>
                    {s.type_caps.map((t) => `${t.name} ${t.capacity == null ? "no limit" : t.capacity}`).join(" + ")}
                    {eff != null ? ` = ${eff}` : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </>
      ) : (
        <div className="st-card p-4" data-testid="date-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-[#8a8a8a]">Single-session date</div>
              <div className="mt-1 text-lg font-bold">
                <span className="text-[#dc2626]">{perf.date.tickets_sold}</span> /{" "}
                <span>{perf.date.effective_capacity == null ? "∞" : perf.date.effective_capacity}</span> tickets sold
              </div>
              <div className="text-sm text-[#dadada] mt-1">{poundsFromPence(perf.date.revenue_pence)} gross</div>
            </div>
            <button className="st-btn" onClick={() => onOpenOverride("date", perf.date)} data-testid="override-date">
              <SettingsIcon size={14}/> Override
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddSlotModal({ eventId, dateId, attraction, onClose, onSaved, onErr }) {
  const [suggestion, setSuggestion] = useState(null);
  const [startT, setStartT] = useState("");
  const [endT, setEndT] = useState("");
  const [cap, setCap] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setLocalErr] = useState("");
  const [chainMode, setChainMode] = useState(false);

  const fetchSuggestion = async () => {
    try {
      const r = await api.get(`/events/${eventId}/dates/${dateId}/slots/next-suggestion`);
      setSuggestion(r.data);
      setStartT(r.data.suggested_start);
      setEndT(r.data.suggested_end);
      setCap(r.data.default_capacity);
    } catch (e) { setLocalErr(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { fetchSuggestion(); /* eslint-disable-line */ }, []);

  const save = async () => {
    setBusy(true); setLocalErr("");
    try {
      await api.post(`/events/${eventId}/dates/${dateId}/slots`, {
        start_time: startT, end_time: endT, max_capacity: Number(cap),
      });
      if (chainMode) {
        await fetchSuggestion();
        onSaved();
      } else { onSaved(); onClose(); }
    } catch (e) {
      const msg = formatErr(e.response?.data?.detail) || e.message;
      setLocalErr(msg);
      onErr && onErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4 st-fade-up" data-testid="add-slot-modal">
      <div className="st-card w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Add next time slot</h2>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-white" data-testid="add-slot-close"><X size={18}/></button>
        </div>
        {suggestion ? (
          <div className="space-y-3">
            <div className="text-sm text-[#bbb]">
              Suggested next slot based on your last slot end + interval:
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs text-[#8a8a8a]">Start</span><input type="time" className="st-input mt-1" value={startT} onChange={(e) => setStartT(e.target.value)} data-testid="new-slot-start"/></label>
              <label className="block"><span className="text-xs text-[#8a8a8a]">End</span><input type="time" className="st-input mt-1" value={endT} onChange={(e) => setEndT(e.target.value)} data-testid="new-slot-end"/></label>
            </div>
            <label className="block"><span className="text-xs text-[#8a8a8a]">Capacity</span><input type="number" min="1" className="st-input mt-1" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="new-slot-cap"/></label>
            <div className="text-[10px] text-[#7a7a7a] st-mono">Preview: {formatSlotTime(startT, endT, attraction)}</div>
            {err && <div className="text-sm text-[#fca5a5]">{err}</div>}
            <label className="flex items-center gap-2 cursor-pointer text-xs text-[#bbb]">
              <input type="checkbox" checked={chainMode} onChange={(e) => setChainMode(e.target.checked)} data-testid="chain-mode"/>
              Keep modal open to add another after this one
            </label>
            <div className="flex justify-end gap-2 mt-3">
              <button className="st-btn st-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="st-btn" onClick={save} disabled={busy || !startT || !endT} data-testid="new-slot-save">{busy ? "Saving…" : "Add slot"}</button>
            </div>
          </div>
        ) : <div className="text-[#8a8a8a] text-sm">Loading suggestion…</div>}
      </div>
    </div>
  );
}

function OverridePanel({ target, attraction, eventId, dateHardCap, siblings, onClose, onSaved, onMsg, onErr }) {
  const { kind, obj, basePrice, ticketTypeId } = target;
  const perType = !!ticketTypeId;
  const defaultCap = obj.max_capacity;            // per-type: the ticket type's event-wide max_per_slot (may be null = unlimited)
  const defaultPrice = basePrice;
  const sold = obj.tickets_sold ?? 0;

  const [capVal, setCapVal] = useState(obj.capacity_override ?? (defaultCap ?? ""));
  const [priceVal, setPriceVal] = useState(((obj.price_override_pence ?? basePrice) / 100).toFixed(2));
  const [disabled, setDisabled] = useState(!!obj.disabled);
  const [confirmOversell, setConfirmOversell] = useState(false);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const capEmpty = capVal === "" || capVal == null;
  const belowSold = !capEmpty && Number(capVal) < sold;

  // Hard-cap oversell warning only applies to shared (all-types) overrides. Per-type caps are independent.
  let projectedTotal = null, hcExceeded = false;
  if (!perType && kind === "slot" && dateHardCap != null && Array.isArray(siblings)) {
    const siblingTotal = siblings.reduce((sum, s) => sum + (s.slot_total ?? s.max_capacity ?? 0), 0);
    projectedTotal = siblingTotal + Number(capVal || 0);
    hcExceeded = projectedTotal > dateHardCap;
  } else if (!perType && kind === "date" && obj.hard_cap != null) {
    hcExceeded = Number(capVal || 0) > obj.hard_cap;
  }

  const url = kind === "slot" ? `/time-slots/${obj.id}/override` : `/event-dates/${obj.id}/override`;

  const submit = async (resetCap, resetPrice) => {
    setErr(""); setBusy(true);
    const body = {};
    if (ticketTypeId) body.ticket_type_id = ticketTypeId;
    if (resetCap || capEmpty) body.reset_capacity = true;
    else body.capacity_override = Number(capVal);
    if (resetPrice) body.reset_price = true;
    else body.price_override_pence = Math.round(parseFloat(priceVal || "0") * 100);
    if (perType) body.disabled = disabled;
    if (hcExceeded) body.confirm_oversell = true;
    try { await api.patch(url, body); onSaved(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const togglePause = async () => {
    if (kind !== "slot") return;
    setBusy(true);
    try {
      await api.patch(`/time-slots/${obj.id}/status`, { status: obj.is_paused ? "active" : "paused" });
      onMsg && onMsg(obj.is_paused ? "Slot unpaused" : "Slot paused");
      onSaved();
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const deleteSlot = async () => {
    if (kind !== "slot") return;
    const ok = await confirmDialog({
      title: "Delete time slot?",
      description: `Are you sure you want to delete the ${formatSlotTime(obj.start_time, obj.end_time, attraction)} slot? This cannot be undone.`,
      confirmLabel: "Yes, delete slot", destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try { await api.delete(`/time-slots/${obj.id}`); onMsg && onMsg("Slot deleted"); onSaved(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const canDelete = kind === "slot" && sold === 0;

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-end md:justify-end st-fade-up" onClick={onClose} data-testid="override-panel">
      <div className="st-card w-full md:max-w-md p-6 m-0 md:m-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold">Override settings</h2>
            <div className="text-sm text-[#8a8a8a]">{kind === "slot" ? formatSlotTime(obj.start_time, obj.end_time, attraction) : obj.date}</div>
          </div>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-white" data-testid="override-close"><X size={18}/></button>
        </div>

        {kind === "slot" && !perType && (
          <div className="flex gap-2 mb-4">
            <button className="st-btn st-btn-ghost text-xs" onClick={togglePause} disabled={busy} data-testid="toggle-pause-slot">
              {obj.is_paused ? <><Play size={12}/> Unpause slot</> : <><Pause size={12}/> Pause slot</>}
            </button>
            {canDelete && (
              <button className="st-btn st-btn-ghost text-xs text-[#fca5a5]" onClick={deleteSlot} disabled={busy} data-testid="delete-slot">
                <Trash2 size={12}/> Delete slot
              </button>
            )}
          </div>
        )}

        {perType && (
          <section className="mb-6">
            <h3 className="text-xs uppercase tracking-[0.18em] text-[#dc2626] mb-2">Availability — this ticket type</h3>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={disabled} onChange={(e) => setDisabled(e.target.checked)} data-testid="override-disabled" />
              Not available at this slot
            </label>
            <p className="text-[11px] text-[#7a7a7a] mt-1">When on, this ticket type is completely hidden from customers for this time slot and can't be purchased. Existing sales are unaffected. Press “Save override” to apply.</p>
          </section>
        )}

        <section className="mb-6">
          <h3 className="text-xs uppercase tracking-[0.18em] text-[#dc2626] mb-2">Capacity{perType ? " — this ticket type" : ""}</h3>
          <div className="text-xs text-[#8a8a8a] mb-2">Default: <span className="text-[#dadada] font-mono">{defaultCap == null ? "Unlimited" : defaultCap}</span> · Sold: <span className="text-[#dadada] font-mono">{sold}</span></div>
          <input type="number" min={sold} className="st-input" value={capVal} placeholder={perType ? "Leave blank for unlimited" : ""} onChange={(e) => setCapVal(e.target.value)} data-testid="override-cap" />
          {perType && <p className="text-[11px] text-[#7a7a7a] mt-1">Independent per-slot cap for this ticket type. Blank = no cap for this type on this slot.</p>}
          {belowSold && <div className="mt-2 text-sm text-[#fca5a5] flex items-center gap-2"><AlertTriangle size={14}/> Cannot set below tickets already sold ({sold}). Minimum is {sold}.</div>}
          {hcExceeded && !belowSold && (
            <div className="mt-2 p-2 rounded-md bg-[#1a1409] border border-[#facc1544]" data-testid="oversell-warning">
              <div className="text-sm text-[#facc15] flex items-center gap-2"><AlertTriangle size={14}/>
                {kind === "slot"
                  ? <>Setting this slot to {capVal} brings total date capacity to <strong>{projectedTotal}</strong>, exceeding the date hard cap of <strong>{dateHardCap}</strong>.</>
                  : <>Capacity exceeds the date hard cap of <strong>{obj.hard_cap}</strong>.</>}
              </div>
              <label className="flex items-center gap-2 mt-2 cursor-pointer text-xs text-[#facc15]">
                <input type="checkbox" checked={confirmOversell} onChange={(e) => setConfirmOversell(e.target.checked)} data-testid="confirm-oversell" />
                Yes, I want to exceed the hard cap for this slot.
              </label>
            </div>
          )}
          <button className="text-xs st-link mt-2 inline-flex items-center gap-1" onClick={() => submit(true, false)} disabled={busy} data-testid="reset-capacity">
            <RotateCcw size={11}/> Reset to default ({defaultCap == null ? "Unlimited" : defaultCap})
          </button>
        </section>

        <section className="mb-6">
          <h3 className="text-xs uppercase tracking-[0.18em] text-[#dc2626] mb-2">Price{perType ? " — this ticket type" : ""}</h3>
          <div className="text-xs text-[#8a8a8a] mb-2">Default base: <span className="text-[#dadada] font-mono">{poundsFromPence(defaultPrice)}</span></div>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-[#7a7a7a]">£</span>
            <input type="number" step="0.01" className="st-input pl-7" value={priceVal} onChange={(e) => setPriceVal(e.target.value)} data-testid="override-price" />
          </div>
          <p className="text-[11px] text-[#7a7a7a] mt-2">This overrides the base price for this {kind} only. Dynamic pricing tiers still apply on top of this override.</p>
          <button className="text-xs st-link mt-2 inline-flex items-center gap-1" onClick={() => submit(false, true)} disabled={busy} data-testid="reset-price">
            <RotateCcw size={11}/> Reset to default
          </button>
        </section>

        {err && <div className="text-sm text-[#fca5a5] mb-3">{err}</div>}

        <div className="flex justify-end gap-2 pt-3 border-t border-[#1a1a1a]">
          <button className="st-btn st-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="st-btn" disabled={busy || belowSold || (hcExceeded && !confirmOversell)}
                  onClick={() => submit(false, false)} data-testid="override-save">
            {busy ? "Saving…" : "Save override"}
          </button>
        </div>
      </div>
    </div>
  );
}


function BulkEditModal({ eventId, ticketType, dateId, dateLabel, slots, attraction, onClose, onSaved, onErr }) {
  const [scope, setScope] = useState("current_date");        // current_date | all_dates
  const [slotMode, setSlotMode] = useState("all");           // all | selected
  const [picked, setPicked] = useState(() => new Set());
  const [capAction, setCapAction] = useState("none");        // none | set | reset
  const [capVal, setCapVal] = useState("");
  const [priceAction, setPriceAction] = useState("none");    // none | set | reset
  const [priceVal, setPriceVal] = useState((((ticketType?.base_price_pence) ?? 0) / 100).toFixed(2));
  const [availAction, setAvailAction] = useState("none");    // none | disable | enable
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const togglePick = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const nothing = capAction === "none" && priceAction === "none" && availAction === "none";
  const noSlots = scope === "current_date" && slotMode === "selected" && picked.size === 0;

  const submit = async () => {
    setErr(""); setBusy(true);
    const body = {
      scope, date_id: dateId,
      slot_ids: scope === "current_date" && slotMode === "selected" ? [...picked] : null,
      capacity_action: capAction,
      capacity_value: capAction === "set" ? Math.max(0, parseInt(capVal || "0", 10)) : null,
      price_action: priceAction,
      price_value_pence: priceAction === "set" ? Math.round(parseFloat(priceVal || "0") * 100) : null,
      availability_action: availAction,
    };
    try {
      const r = await api.post(`/events/${eventId}/ticket-types/${ticketType.id}/bulk-override`, body);
      onSaved(r.data?.updated ?? 0);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="bulk-edit-modal" onClick={onClose}>
      <div className="st-card w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-lg font-bold">Bulk edit — {ticketType?.name}</h2>
          <button onClick={onClose} className="text-[#8a8a8a] hover:text-white"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#8a8a8a] mb-4">Apply a capacity and/or price to many time slots for this ticket type at once.</p>

        <div className="mb-4">
          <div className="text-xs uppercase tracking-[0.18em] text-[#dc2626] mb-2">Apply to</div>
          <div className="flex gap-2">
            <BulkChoice active={scope === "current_date"} onClick={() => setScope("current_date")} testid="bulk-scope-date">This date<span className="block text-[10px] text-[#7a7a7a] normal-case">{dateLabel}</span></BulkChoice>
            <BulkChoice active={scope === "all_dates"} onClick={() => setScope("all_dates")} testid="bulk-scope-all">All dates<span className="block text-[10px] text-[#7a7a7a] normal-case">Every date in this event</span></BulkChoice>
          </div>
        </div>

        {scope === "current_date" && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-[0.18em] text-[#dc2626] mb-2">Time slots</div>
            <div className="flex gap-2 mb-2">
              <BulkChoice active={slotMode === "all"} onClick={() => setSlotMode("all")} testid="bulk-slots-all">All slots</BulkChoice>
              <BulkChoice active={slotMode === "selected"} onClick={() => setSlotMode("selected")} testid="bulk-slots-selected">Selected</BulkChoice>
            </div>
            {slotMode === "selected" && (
              <div className="grid grid-cols-3 gap-1.5 max-h-40 overflow-y-auto p-1" data-testid="bulk-slot-list">
                {slots.map((s) => (
                  <button key={s.id} onClick={() => togglePick(s.id)} data-testid={`bulk-slot-${s.id}`}
                          className={`text-xs px-2 py-1.5 rounded border ${picked.has(s.id) ? "bg-[#dc2626] border-[#dc2626] text-white" : "bg-[#0e0e0e] border-[#222] text-[#bbb]"}`}>
                    {formatSlotTime(s.start_time, s.end_time, attraction)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mb-4">
          <div className="text-xs uppercase tracking-[0.18em] text-[#dc2626] mb-2">Capacity (per slot)</div>
          <div className="flex gap-2 mb-2">
            <BulkChoice active={capAction === "none"} onClick={() => setCapAction("none")} testid="bulk-cap-none">Don't change</BulkChoice>
            <BulkChoice active={capAction === "set"} onClick={() => setCapAction("set")} testid="bulk-cap-set">Set to…</BulkChoice>
            <BulkChoice active={capAction === "reset"} onClick={() => setCapAction("reset")} testid="bulk-cap-reset">Reset</BulkChoice>
          </div>
          {capAction === "set" && <input type="number" min={0} className="st-input" value={capVal} placeholder="e.g. 20" onChange={(e) => setCapVal(e.target.value)} data-testid="bulk-cap-value" />}
          {capAction === "set" && <p className="text-[11px] text-[#7a7a7a] mt-1">Slots that already sold more than this keep their sold count as the minimum.</p>}
        </div>

        <div className="mb-4">
          <div className="text-xs uppercase tracking-[0.18em] text-[#dc2626] mb-2">Price (per slot)</div>
          <div className="flex gap-2 mb-2">
            <BulkChoice active={priceAction === "none"} onClick={() => setPriceAction("none")} testid="bulk-price-none">Don't change</BulkChoice>
            <BulkChoice active={priceAction === "set"} onClick={() => setPriceAction("set")} testid="bulk-price-set">Set to…</BulkChoice>
            <BulkChoice active={priceAction === "reset"} onClick={() => setPriceAction("reset")} testid="bulk-price-reset">Reset</BulkChoice>
          </div>
          {priceAction === "set" && (
            <div className="flex items-center gap-2">
              <span className="text-[#8a8a8a]">£</span>
              <input type="number" min={0} step="0.01" className="st-input" value={priceVal} onChange={(e) => setPriceVal(e.target.value)} data-testid="bulk-price-value" />
            </div>
          )}
        </div>

        <div className="mb-4">
          <div className="text-xs uppercase tracking-[0.18em] text-[#dc2626] mb-2">Availability (per slot)</div>
          <div className="flex gap-2 mb-2">
            <BulkChoice active={availAction === "none"} onClick={() => setAvailAction("none")} testid="bulk-avail-none">Don't change</BulkChoice>
            <BulkChoice active={availAction === "disable"} onClick={() => setAvailAction("disable")} testid="bulk-avail-disable">Make unavailable</BulkChoice>
            <BulkChoice active={availAction === "enable"} onClick={() => setAvailAction("enable")} testid="bulk-avail-enable">Make available</BulkChoice>
          </div>
          <p className="text-[11px] text-[#7a7a7a]">“Make unavailable” hides this ticket type on the targeted slots so customers can't book it there.</p>
        </div>

        {err && <div className="text-sm text-[#fca5a5] mb-3">{err}</div>}
        <div className="flex justify-end gap-2 pt-3 border-t border-[#1a1a1a]">
          <button className="st-btn st-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="st-btn" disabled={busy || nothing || noSlots} onClick={submit} data-testid="bulk-apply">
            {busy ? "Applying…" : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

function BulkChoice({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
            className={`flex-1 text-left text-xs uppercase tracking-wide px-3 py-2 rounded border transition-colors ${active ? "bg-[#1a0d0d] border-[#dc2626] text-white" : "bg-[#0e0e0e] border-[#222] text-[#bbb] hover:border-[#444]"}`}>
      {children}
    </button>
  );
}
